import { Employee } from "@/types/v2";
import { user as userActions } from "@/repositories/user";
import { employee as employeeActions } from "@/repositories/employee";

interface ActivateEmployeeAccountArgs {
  shopId: string;
  employee: Pick<Employee, "id" | "email" | "firstName" | "lastName">;
  /**
   * true: generate a new password and email it (also creates the account if
   * it does not exist). false: only re-enable the existing account.
   */
  resetPassword: boolean;
}

/**
 * The account is enabled (and any new password sent) but the employee's role
 * could not be saved, so the caller must not report the activation as failed.
 */
export class RoleNotSavedError extends Error {
  constructor(public readonly cause: unknown) {
    super("Account activated, but the employee's role was not saved");
    this.name = "RoleNotSavedError";
  }
}

/**
 * The one activate sequence for the Employees table: get the auth account
 * enabled (with or without a new password), then give the employee the
 * viewOnly role back. Deactivating only clears the role on the employee doc;
 * the auth account keeps its old claims, so the server demotes it to viewOnly
 * before enabling (reactivateAuthUser) and this write brings the doc back in
 * line.
 */
export const activateEmployeeAccount = async ({
  shopId,
  employee,
  resetPassword,
}: ActivateEmployeeAccountArgs) => {
  const authUser = resetPassword
    ? await userActions.createAuthAccount(
        employee.email,
        employee.firstName,
        employee.lastName,
        shopId,
        employee.id
      )
    : await userActions.reactivateAuthAccount(shopId, employee.id);

  try {
    await employeeActions.updateEmployeeRole(shopId, employee.id, "viewOnly");
  } catch (e) {
    throw new RoleNotSavedError(e);
  }

  return {
    authAccountStatus: authUser.data.disabled
      ? ("inactive" as const)
      : ("active" as const),
    shopHeroRole: "viewOnly" as const,
    shopHeroAuthUid: authUser.data.uid,
    // `emailSent` is only present once the backend confirms the email
    // actually sent; a not-yet-redeployed backend won't include it at all,
    // so treat absent the same as false rather than assuming success.
    emailSent: resetPassword ? !!authUser.data.emailSent : false,
  };
};
